import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
//#region src/components/Calendar/utils/date-grid.ts
dayjs.extend(utc);
dayjs.extend(timezone);
var MINUTES_PER_DAY = 1440;
/** Blocks shorter than this render at this length so they stay clickable. */
var MIN_SEGMENT_MINUTES = 20;
function zoned(ms, timeZone) {
	return timeZone ? dayjs(ms).tz(timeZone) : dayjs(ms);
}
/** Local (or `timeZone`) midnight of the day containing `ms`. */
function startOfDay(ms, timeZone) {
	if (timeZone) return dayjs(ms).tz(timeZone).startOf("day").valueOf();
	const d = new Date(ms);
	d.setHours(0, 0, 0, 0);
	return d.getTime();
}
/** Add calendar days preserving wall-clock time (DST-safe). */
function addDays(ms, days, timeZone) {
	if (timeZone) return dayjs(ms).tz(timeZone).add(days, "day").valueOf();
	const d = new Date(ms);
	d.setDate(d.getDate() + days);
	return d.getTime();
}
function addWeeks(ms, weeks, timeZone) {
	return addDays(ms, weeks * 7, timeZone);
}
/** Add months, normalizing to the 1st so short months can't skip. */
function addMonths(ms, months, timeZone) {
	if (timeZone) return dayjs(ms).tz(timeZone).date(1).add(months, "month").valueOf();
	const d = new Date(ms);
	d.setDate(1);
	d.setMonth(d.getMonth() + months);
	return d.getTime();
}
function isSameDay(a, b, timeZone) {
	return startOfDay(a, timeZone) === startOfDay(b, timeZone);
}
/** Wall-clock minutes since midnight — DST-independent positioning. */
function minutesOfDay(ms, timeZone) {
	if (timeZone) {
		const d = dayjs(ms).tz(timeZone);
		return d.hour() * 60 + d.minute();
	}
	const d = new Date(ms);
	return d.getHours() * 60 + d.getMinutes();
}
function calendarDate(ms, timeZone) {
	return zoned(ms, timeZone).date();
}
function calendarMonth(ms, timeZone) {
	return zoned(ms, timeZone).month();
}
function calendarWeekdayShort(ms, timeZone) {
	return zoned(ms, timeZone).format("ddd");
}
/** 7 start-of-day timestamps for the Sunday-start week containing `anchorMs`. */
function weekDays(anchorMs, timeZone) {
	if (timeZone) {
		const start = dayjs(anchorMs).tz(timeZone).startOf("day");
		const first = start.subtract(start.day(), "day");
		return Array.from({ length: 7 }, (_, i) => first.add(i, "day").valueOf());
	}
	const d = new Date(startOfDay(anchorMs));
	d.setDate(d.getDate() - d.getDay());
	const first = d.getTime();
	return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}
/**
* 42 start-of-day timestamps (6 fixed Sunday-start rows) covering the month
* containing `anchorMs` — fixed row count so the grid height never jumps.
*/
function monthGridDays(anchorMs, timeZone) {
	if (timeZone) {
		const firstOfMonth = dayjs(anchorMs).tz(timeZone).startOf("month");
		const first = firstOfMonth.subtract(firstOfMonth.day(), "day");
		return Array.from({ length: 42 }, (_, i) => first.add(i, "day").valueOf());
	}
	const d = new Date(startOfDay(anchorMs));
	d.setDate(1);
	d.setDate(d.getDate() - d.getDay());
	const first = d.getTime();
	return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}
/** Inclusive-start/exclusive-end instant range covering the visible grid. */
function visibleRange(anchorMs, view, timeZone) {
	const days = view === "week" ? weekDays(anchorMs, timeZone) : monthGridDays(anchorMs, timeZone);
	return {
		fromMs: days[0],
		toMs: addDays(days[days.length - 1], 1, timeZone)
	};
}
/** "Jul 5 – 11, 2026" · "Jun 28 – Jul 4, 2026" · "Dec 28, 2025 – Jan 3, 2026" · "July 2026". */
function rangeLabel(anchorMs, view, timeZone) {
	if (view === "month") return zoned(anchorMs, timeZone).format("MMMM YYYY");
	const [firstMs] = weekDays(anchorMs, timeZone);
	const first = zoned(firstMs, timeZone);
	const last = zoned(addDays(firstMs, 6, timeZone), timeZone);
	if (first.year() !== last.year()) return `${first.format("MMM D, YYYY")} – ${last.format("MMM D, YYYY")}`;
	if (first.month() !== last.month()) return `${first.format("MMM D")} – ${last.format("MMM D, YYYY")}`;
	return `${first.format("MMM D")} – ${last.format("D, YYYY")}`;
}
/** Split an event across the given day columns, clipping at midnights. */
function splitIntoDaySegments(event, days, timeZone) {
	const segments = [];
	for (const dayStartMs of days) {
		const dayEndMs = addDays(dayStartMs, 1, timeZone);
		if (event.startsAt >= dayEndMs || event.endsAt <= dayStartMs) continue;
		const clippedStart = event.startsAt < dayStartMs;
		const clippedEnd = event.endsAt > dayEndMs;
		const startMin = clippedStart ? 0 : minutesOfDay(event.startsAt, timeZone);
		let endMin = clippedEnd || event.endsAt === dayEndMs ? MINUTES_PER_DAY : minutesOfDay(event.endsAt, timeZone);
		endMin = Math.max(endMin, Math.min(startMin + MIN_SEGMENT_MINUTES, MINUTES_PER_DAY));
		segments.push({
			clippedEnd,
			clippedStart,
			dayStartMs,
			endMin,
			event,
			startMin
		});
	}
	return segments;
}
/**
* Whether an event occupies more than one day column — i.e. it runs PAST the
* midnight following its start. Same strict boundary as `splitIntoDaySegments`
* (`endsAt > dayEndMs`), so an event ending exactly at midnight belongs to its
* start day alone. Consumers describing an event ("crosses midnight") MUST use
* this rather than comparing calendar days, or their words will disagree with
* the segments the grid actually renders.
*/
function crossesMidnight(startsAt, endsAt, timeZone) {
	return endsAt > addDays(startOfDay(startsAt, timeZone), 1, timeZone);
}
/**
* Bucket segments by their day column, each day's list ordered by start minute
* — so a continuation segment (which starts at minute 0) leads its day. Works
* for both plain and lane-assigned segments; both grids render day-by-day.
*/
function groupSegmentsByDay(segments) {
	const byDay = /* @__PURE__ */ new Map();
	for (const seg of segments) {
		const list = byDay.get(seg.dayStartMs) ?? [];
		list.push(seg);
		byDay.set(seg.dayStartMs, list);
	}
	for (const list of byDay.values()) list.sort((a, b) => a.startMin - b.startMin);
	return byDay;
}
/**
* Assign side-by-side lanes to overlapping segments, per day. Segments in the
* same overlap cluster share a `laneCount` so widths divide evenly; freed
* lanes are reused within a cluster.
*/
function layoutLanes(segments) {
	const byDay = /* @__PURE__ */ new Map();
	for (const s of segments) {
		const list = byDay.get(s.dayStartMs) ?? [];
		list.push(s);
		byDay.set(s.dayStartMs, list);
	}
	const out = [];
	for (const list of byDay.values()) {
		const sorted = [...list].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
		const laneEnds = [];
		let cluster = [];
		let clusterEnd = -1;
		const flush = () => {
			for (const seg of cluster) seg.laneCount = laneEnds.length;
			out.push(...cluster);
			cluster = [];
			laneEnds.length = 0;
		};
		for (const seg of sorted) {
			if (cluster.length > 0 && seg.startMin >= clusterEnd) flush();
			let lane = laneEnds.findIndex((end) => end <= seg.startMin);
			if (lane === -1) {
				lane = laneEnds.length;
				laneEnds.push(seg.endMin);
			} else laneEnds[lane] = seg.endMin;
			cluster.push({
				...seg,
				lane,
				laneCount: 0
			});
			clusterEnd = Math.max(clusterEnd, seg.endMin);
		}
		flush();
	}
	return out;
}
//#endregion
export { MINUTES_PER_DAY, addDays, addMonths, addWeeks, calendarDate, calendarMonth, calendarWeekdayShort, crossesMidnight, groupSegmentsByDay, isSameDay, layoutLanes, minutesOfDay, monthGridDays, rangeLabel, splitIntoDaySegments, startOfDay, visibleRange, weekDays };

//# sourceMappingURL=date-grid.js.map